import axios from "axios";
import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { EDifficulty } from "../interfaces";

const speedControlApi = axios.create({
  baseURL: `${process.env.REACT_APP_SPEED_CONTROL_HOST}/api`,
  headers: {
    "x-api-token": process.env.REACT_APP_SPEED_CONTROL_API_TOKEN || "",
  },
});

export interface ISpeedControlData {
  kartData: IKartData[],
  thresholdData: ThresholdRecord,
  speedChangeLogs: SpeedChangeLog[]
}

export interface IKartData {
  kart: number;
  rimoData: RimoData;
  racefacerData?: RaceFacerData
  calculatedData: CalculatedKartData;
}

type Sector = "s1" | "s2" | "s3";

export enum EKartRacingStatus {
  racing = 'Main Race',
  offTrack = 'Off Track',
  speedingUp = 'Speeding Up',
  inQueue = 'In Queue',
  penalised = 'Penalised',
}

interface CalculatedKartData {
  status: EKartRacingStatus | "REFERENCE";
  old_display: string;
  old_speedset: number;
  top_speed: number; // Top speed the kart has reached in the session
  new_speedset: number;
  new_display: string;
  penalty: Date | null;
  is_penalised: boolean;
  last_speed_change: Date | null;
  next_sector: Sector;
  eval_result: string;
}

interface RaceFacerData {
	name: string,
  best_time: string;
  best_time_raw: number;
  last_time: string;
  last_time_raw: number;
  heat_best_time: string;
  heat_best_time_raw: number;
  prev_time_raw: number;
  pos: number;
  time_left_clock: string;
  s1: string;
  s2: string;
  s3: string;
  s4: string;
}

interface RimoData {
  serial: number;
  soc: number;
  speedset: number;
  kart: string;
}

interface ISpeedControlContextType {
  isLoading: boolean;
  isScriptRunning: boolean;
  difficulty: EDifficulty;
  isChangingScriptStatus: boolean;

  currentKarts: IKartData[];
  thresholds: ThresholdRecord;
  speedChangeLogs: SpeedChangeLog[];

  startScript: () => Promise<any>;
  stopScript: () => Promise<any>;
  changeDifficulty: (difficulty: EDifficulty) => Promise<any>;
}

type ThresholdValue = {
  eligibility: number;
  speedset: number;
}

export interface ThresholdRecord {
  difficultyUsed: number;
  trackToThreshold: Record<number, Thresholds>
}

export interface Thresholds {
  trackName: string,
  s1: ThresholdValue[];
  s2: ThresholdValue[];
  s3: ThresholdValue[];
  lap: ThresholdValue[];
}

const DEFAULT_THRESHOLDS: ThresholdRecord = {
  difficultyUsed: 1,
  trackToThreshold: {}
}

export interface SpeedChangeLog {
  kart: number,
  time: Date,

  oldSpeedsetId: number,
  oldSpeedsetValue: number,
  newSpeedsetId: number,
  newSpeedsetValue: number,

  reason: string
}

const SpeedControlContext = createContext<ISpeedControlContextType>(
  undefined as unknown as ISpeedControlContextType
);

const SpeedControlContextProvider: FC<
  PropsWithChildren<{ value: ISpeedControlContextType }>
> = ({ value, children }) => {
  return (
    <SpeedControlContext.Provider value={value}>
      {children}
    </SpeedControlContext.Provider>
  );
};

export const useSpeedControl = () => {
  return useContext<ISpeedControlContextType>(SpeedControlContext);
};

interface Type {}
const SpeedControlProvider: FC<PropsWithChildren<Type>> = ({ children }) => {
  const [isChangingScriptStatus, setIsChangingScriptStatus] = useState(false);
  const [difficulty, setDifficulty] = useState<EDifficulty>(EDifficulty.Normal);
  const [isScriptRunning, setIsScriptRunning] = useState(false);

  const [currentKarts, setCurrentKarts] = useState<IKartData[]>([]);
  const [thresholds, setThresholds] = useState<ThresholdRecord>(DEFAULT_THRESHOLDS);
  const [speedChangeLogs, setSpeedChangeLogs] = useState<SpeedChangeLog[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const fetchCurrentKartData = useCallback((withLoader = false) => {
    withLoader && setIsLoading(true);
    speedControlApi
      .get("/speed-control/data")
      .then(({ data }) => {
        if (!data.success || !data.data) return;
        
        const speedControlData: ISpeedControlData = data.data;
        
        speedControlData.kartData && setCurrentKarts(speedControlData.kartData);
        speedControlData.thresholdData && setThresholds(speedControlData.thresholdData);
        speedControlData.speedChangeLogs && setSpeedChangeLogs(speedControlData.speedChangeLogs);
      })
      .catch(console.error)
      .finally(() => {
        withLoader && setIsLoading(false);
      });
  }, []);

  const checkScriptStatus = useCallback(() => {
    speedControlApi
      .get("/speed-control/status")
      .then((scriptStatusResponse: any) => {
        if (scriptStatusResponse.data.success) {
          setIsScriptRunning(scriptStatusResponse.data.data.isRunning);
          setDifficulty(
            scriptStatusResponse.data.data.difficulty || EDifficulty.Normal
          );
        }
      })
      .catch(console.error);
  }, []);
  const startScript = useCallback(async () => {
    setIsChangingScriptStatus(true);
    try {
      const startScriptResponse = await speedControlApi.post("/speed-control/script/start", {
        difficulty: EDifficulty.Normal,
      });

      if (startScriptResponse.data.success) {
        setIsScriptRunning(true);
      }
    } catch (error) {
      console.log("error", error);
      alert(error);
    } finally {
      setIsChangingScriptStatus(false);
    }
  }, []);
  const stopScript = useCallback(async () => {
    setIsChangingScriptStatus(true);
    try {
      const stopScriptResponse = await speedControlApi.post("/speed-control/script/stop");
      if (stopScriptResponse.data.success) {
        setIsScriptRunning(false);
      }
    } catch (error) {
      console.log("error", error);
      alert(error);
    } finally {
      setIsChangingScriptStatus(false);
    }
  }, []);

  const changeDifficulty = useCallback(async (difficulty: EDifficulty) => {
    try {
      await speedControlApi.post("/speed-control/difficulty", {
        difficulty,
      });
    } catch (error) {
      console.log("error", error);
      alert(error);
    }
  }, []);

  useEffect(() => {
    fetchCurrentKartData(true);
    checkScriptStatus();
  }, [fetchCurrentKartData, checkScriptStatus]);

  useEffect(() => {
    const intervals: NodeJS.Timer[] = [];
    intervals.push(setInterval(fetchCurrentKartData, 5 * 1000));
    intervals.push(setInterval(checkScriptStatus, 1000));
    return () => {
      intervals.forEach((interval) => {
        if (interval) {
          clearInterval(interval);
        }
      });
    };
  }, [fetchCurrentKartData, checkScriptStatus]);

  return (
    <SpeedControlContextProvider
      value={{
        isLoading,
        isScriptRunning,
        difficulty,
        isChangingScriptStatus,

        currentKarts,
        thresholds,
        speedChangeLogs,
        startScript,
        stopScript,
        changeDifficulty,
      }}
    >
      {children}
    </SpeedControlContextProvider>
  );
};

export default SpeedControlProvider;
